import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import UserNav from '../../components/navbar/user-nav';
import AdminSidebar from '../../components/admin-sidebar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';

interface Proyecto {
  id: string;
  nombre: string;
  area_m2: number;
  direccion: string;
  inmobiliaria_id: string;
  amenidades?: string[];
  nombreInmobiliaria?: string;
}

export default function MyProjects() {
  const navigate = useNavigate();
  const [user, setUser]       = useState<any>(null);
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);

  /* ───── 1) Detectar usuario (asociado/asesor) ───── */
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        return;
      }
  
      const email = fbUser.email;
  
      // Buscar en asociado
      let snap = await getDocs(
        query(collection(db, 'asociado'), where('correo', '==', email))
      );
      if (!snap.empty) {
        const docData = snap.docs[0].data();
        const inmobiliariaDocId = docData.inmobiliaria_doc_id; // Usar doc.id
        let nombreInmobiliaria = '';

        if (inmobiliariaDocId) {
          // Buscar directamente por doc.id
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            nombreInmobiliaria = inmoSnap.data().nombre || '';
          }
        }

        setUser({
          tipo: 'asociado',
          ...docData,
          inmobiliaria_doc_id: inmobiliariaDocId,
          inmobiliaria_nombre: nombreInmobiliaria
        });
        return;
      }
  
      // Buscar en asesor
      snap = await getDocs(
        query(collection(db, 'asesor'), where('correo', '==', email))
      );
      if (!snap.empty) {
        const docData = snap.docs[0].data();
        const inmobiliariaDocId = Array.isArray(docData.inmobiliaria_doc_ids)
          ? docData.inmobiliaria_doc_ids[0]
          : docData.inmobiliaria_doc_ids;
        let nombreInmobiliaria = '';

        if (inmobiliariaDocId) {
          // Buscar directamente por doc.id
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            nombreInmobiliaria = inmoSnap.data().nombre || '';
          }
        }

        setUser({
          tipo: 'asesor',
          ...docData,
          inmobiliaria_doc_id: inmobiliariaDocId,
          inmobiliaria_nombre: nombreInmobiliaria
        });
      }
    });
  
    return () => unsub();
  }, []);
  

  /* ───── 2) Cargar proyectos de la inmobiliaria ───── */
  useEffect(() => {
    const obtener = async () => {
      if (!user?.inmobiliaria_doc_id) { setLoading(false); return; }

      const q = query(
        collection(db, 'proyectos'),
        where('inmobiliaria_doc_id', '==', user.inmobiliaria_doc_id)
      );
      const snap = await getDocs(q);

      const list = await Promise.all(
        snap.docs.map(async (d) => {
          const proj = d.data() as Proyecto;
          let nombreInm = 'Desconocida';

          if (proj.inmobiliaria_id) {
            // Buscar directamente por doc.id
            const { doc: docFn, getDoc } = await import('firebase/firestore');
            const inmoDocRef = docFn(db, 'inmobiliaria', proj.inmobiliaria_id);
            const inmoSnap = await getDoc(inmoDocRef);
            if (inmoSnap.exists()) {
              nombreInm = inmoSnap.data().nombre || 'Desconocida';
            }
          }

          return { ...proj, id: d.id, nombreInmobiliaria: nombreInm };
        })
      );

      setProyectos(list);
      setLoading(false);
    };
    obtener();
  }, [user]);

  /* ───── 3) Renderizado condicional ───── */
  const esAsociado = user?.tipo === 'asociado';
  const puedeVer   = !!user?.inmobiliaria_doc_id && (esAsociado || user?.tipo === 'asesor');

  return (
    <>
      <UserNav
        nombreUsuario={user?.nombre || 'Desconocido'}
        nombreInmobiliaria={user?.inmobiliaria_nombre || 'Inmobiliaria no asignada'}
      />

      <div className="container-fluid">
        <div className="row">

          {/* Sidebar */}
          <div className="col-lg-3 col-md-12">
            <AdminSidebar
              show={showSidebar}
              setShow={setShowSidebar}
              nombre={user?.nombre}
              inmobiliaria={user?.inmobiliaria_nombre}
            />
          </div>

          {/* Contenido */}
          <div className="col-lg-9 col-md-12 my-5">
            {!puedeVer ? (
              /* ── Banner sin permisos ── */
              <div className="alert alert-info text-center">
                <h5>¡Hola!</h5>
                <p>
                  De momento no tienes proyectos propios para mostrar.
                  Puedes explorar todos los proyectos disponibles en la vista general.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/project-list')}
                >
                  Ir a Proyectos Generales
                </button>
              </div>
            ) : (
              /* ── Lista de proyectos ── */
              <>
                <h2 className="mb-4">Mis Proyectos</h2>
                {loading ? (
                  <p>Cargando proyectos…</p>
                ) : proyectos.length === 0 ? (
                  <p className="text-muted">No tienes proyectos registrados.</p>
                ) : (
                  <div className="row">
                    {proyectos.map((p) => (
                      <div key={p.id} className="col-md-6 mb-4">
                        <div className="card p-3 shadow-sm h-100">
                          <h5>{p.nombre}</h5>
                          <p><strong>Área:</strong> {p.area_m2} m²</p>
                          <p><strong>Inmobiliaria:</strong> {p.nombreInmobiliaria}</p>
                          <p>
                            <strong>Dirección:</strong>{' '}
                            <a href={p.direccion} target="_blank" rel="noopener noreferrer">
                              {p.direccion}
                            </a>
                          </p>
                          <p><strong>Amenidades:</strong> {p.amenidades?.join(', ') || 'N/A'}</p>

                          <div className="d-flex justify-content-between">
                            <Link to={`/view-project/${p.id}`} className="btn btn-primary btn-sm">
                              Ver Detalles
                            </Link>
                            {esAsociado && (
                              <Link to={`/edit-project/${p.id}`} className="btn btn-warning btn-sm">
                                <i className="fa-solid fa-pen-to-square me-1"></i> Editar
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
